import { useCallback, useEffect, useState } from 'react'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import {
  BellIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ForwardIcon,
  HandRaisedIcon,
} from '@heroicons/react/24/outline'
import { Link, useNavigate } from 'react-router'
import { NotificationsService, type NotificationKind, type NotificationRead } from '../../client'
import { relativeTime } from '../../lib/format'
import { useAuth } from '../../providers/auth/useAuth'

const POLL_MS = 60_000

const ICON: Record<NotificationKind, { icon: typeof BellIcon; tone: string }> = {
  run_completed: { icon: CheckCircleIcon, tone: 'text-emerald-600' },
  run_failed: { icon: ExclamationTriangleIcon, tone: 'text-red-600' },
  run_needs_approval: { icon: HandRaisedIcon, tone: 'text-amber-600' },
  run_skipped: { icon: ForwardIcon, tone: 'text-stone-400' },
}

export default function NotificationBell() {
  const { brand } = useAuth()
  const navigate = useNavigate()
  const [items, setItems] = useState<NotificationRead[]>([])
  const [unread, setUnread] = useState(0)
  const brandId = brand?.id

  const load = useCallback(() => {
    if (!brandId) return
    NotificationsService.listNotifications(brandId)
      .then((list) => {
        setItems(list.notifications)
        setUnread(list.unread)
      })
      .catch(() => undefined)
  }, [brandId])

  useEffect(() => {
    load()
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load()
    }, POLL_MS)
    return () => clearInterval(timer)
  }, [load])

  async function markAll() {
    if (!brandId || unread === 0) return
    setUnread(0)
    setItems((current) => current.map((n) => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })))
    await NotificationsService.markRead(brandId, {}).catch(() => load())
  }

  function open(notification: NotificationRead, close: () => void) {
    close()
    if (brandId && !notification.read_at) {
      setUnread((count) => Math.max(0, count - 1))
      setItems((current) =>
        current.map((n) => (n.id === notification.id ? { ...n, read_at: new Date().toISOString() } : n)),
      )
      void NotificationsService.markRead(brandId, { ids: [notification.id] }).catch(() => load())
    }
    void navigate(notification.thread_id ? `/c/${notification.thread_id}` : '/workflows')
  }

  return (
    <Popover>
      <PopoverButton
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        data-tooltip="Notifications"
        data-tooltip-above=""
        onClick={load}
        className="relative flex size-10 shrink-0 items-center justify-center rounded-lg text-stone-500 transition-colors hover:bg-black/5 hover:text-stone-900 md:size-8"
      >
        <BellIcon className="size-4.5" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-purple-700 px-1 text-[9px] font-semibold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </PopoverButton>

      <PopoverPanel
        anchor={{ to: 'top start', gap: 6 }}
        transition
        className="z-50 flex w-80 flex-col overflow-hidden rounded-xl border border-stone-200 bg-white shadow-[0_2px_4px_rgba(28,25,23,0.04),0_18px_44px_-16px_rgba(28,25,23,0.22)] transition duration-100 ease-out [--anchor-max-height:28rem] data-closed:translate-y-1 data-closed:opacity-0"
      >
        {({ close }) => (
          <>
            <div className="flex items-center justify-between border-b border-stone-100 px-4 py-3">
              <p className="text-sm font-medium text-stone-900">Notifications</p>
              <button
                type="button"
                disabled={unread === 0}
                onClick={() => void markAll()}
                className="text-xs text-purple-700 hover:text-purple-900 disabled:text-stone-300"
              >
                Mark all read
              </button>
            </div>
            <ul className="min-h-0 flex-1 overflow-y-auto p-1 [scrollbar-width:thin]">
              {items.length === 0 && (
                <li className="px-3 py-8 text-center text-xs text-stone-500">
                  Scheduled workflow runs report here.
                </li>
              )}
              {items.map((notification) => {
                const { icon: Icon, tone } = ICON[notification.kind]
                return (
                  <li key={notification.id}>
                    <button
                      type="button"
                      onClick={() => open(notification, close)}
                      className="flex w-full gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-stone-100"
                    >
                      <Icon className={`mt-0.5 size-4 shrink-0 ${tone}`} />
                      <span className="min-w-0 flex-1">
                        <span
                          className={`line-clamp-2 text-[13px] ${notification.read_at ? 'text-stone-600' : 'font-medium text-stone-900'}`}
                        >
                          {notification.title}
                        </span>
                        {notification.body && (
                          <span className="block text-xs text-stone-500">{notification.body}</span>
                        )}
                        <span className="block text-[11px] text-stone-400">{relativeTime(notification.created_at)}</span>
                      </span>
                      {!notification.read_at && <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-purple-600" />}
                    </button>
                  </li>
                )
              })}
            </ul>
            <Link
              to="/notifications"
              onClick={() => close()}
              className="border-t border-stone-100 px-4 py-2.5 text-xs text-stone-600 transition-colors hover:bg-stone-50 hover:text-stone-900"
            >
              Notification settings
            </Link>
          </>
        )}
      </PopoverPanel>
    </Popover>
  )
}
